import type { KeyboardEvent, ReactNode } from 'react';
import { Fragment, createElement, forwardRef, useMemo } from 'react';

import type {
  A11y,
  ButtonShape,
  ButtonSize,
  ButtonTheme,
  Classnames,
  Color,
  IconList,
  IconPosition,
} from '@snowit/icetypes';
import type { ButtonClick } from '@snowit/icetypes/events';

import { COLOR } from '@snowit/icetypes';
import testId from '@snowit/utils/testId';

import Icon from '../../Icon';
import Loader from '../../Loaders/LoaderSpinner';

import cx from 'classnames';
import css from './Button.module.scss';

export type TButtonColor = Color | (string & {});

export interface IButton {
  label?: string | ReactNode | ((...args: any[]) => string | ReactNode);
  hint?: string | ReactNode | ((...args: any[]) => string | ReactNode);
  hintPosition?: IconPosition;
  icon?: IconList;
  iconPosition?: IconPosition;
  iconColor?: boolean;
  type?: string;
  theme?: ButtonTheme;
  shape?: ButtonShape;
  size?: ButtonSize;
  bgColor?: TButtonColor;
  labelColor?: TButtonColor;
  noPadding?: boolean;
  loading?: boolean;
  disabled?: boolean;
  name?: string;
  value?: any;
  href?: string;
  onClick?: (params: ButtonClick) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  id?: string;
  classNames?: Classnames<'button' | 'content' | 'labelContent' | 'label' | 'hint' | 'icon'>;
  a11y?: A11y;
  /** Jest/Cypress test string */
  test?: string;
}

const BUTTON_SIZE_V2_MAP: { [x in ButtonSize]?: ButtonSize } = {
  lg: 'qr',
  df: 'tr',
  sm: 'db',
  xs: 'xxl',
};

const BUTTON_HINT_SIZES: NonNullable<ButtonSize>[] = ['db', 'big', 'tr', 'qr'];

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, IButton>(
  (props: IButton, ref: any) => {
    const {
      label,
      hint,
      hintPosition = 'after',
      icon,
      iconPosition = 'before',
      iconColor = true,
      type = 'button',
      theme = 'fill',
      shape = 'default',
      size = 'tr',
      bgColor: _bgColor = 'text',
      labelColor: _labelColor,
      noPadding,
      loading,
      disabled,
      name,
      value,
      href,
      onClick,
      onKeyDown,
      id,
      classNames,
      a11y,
      test,
    } = props;

    // map the legacy size when provided, then use the default.
    const buttonSize: ButtonSize = BUTTON_SIZE_V2_MAP[size] ?? size;

    // Convert supported color tokens to CSS variables while preserving custom color values.
    const bgColor = COLOR.includes(_bgColor as Color) ? `var(--c-${_bgColor})` : _bgColor;

    const labelColor = useMemo(() => {
      if (_labelColor) {
        return COLOR.includes(_labelColor as Color) ? `var(--c-${_labelColor})` : _labelColor;
      }
      if (theme !== 'fill') return bgColor;
      if (_bgColor === 'primary') return 'var(--c-primary-contrast)';
      return 'var(--c-white)';
    }, [_labelColor, theme, _bgColor, bgColor]);

    const handleClick = (event) => {
      if (disabled) return;
      if (typeof onClick === 'function') {
        onClick({ event, name, id, value });
      }
    };

    const renderIcon = () => {
      if (!icon) return null;
      return (
        <Icon className={cx(css.icon, classNames?.icon)} name={icon} a11y={{ hidden: true }} />
      );
    };

    const renderLabel = () => {
      if (!label) return null;
      const _label = typeof label === 'function' ? label() : label;
      if (typeof _label === 'string') {
        return <span className={cx(css.label, classNames?.label)}>{_label}</span>;
      }
      return _label;
    };

    const renderHint = () => {
      if (!hint) return null;
      if (!BUTTON_HINT_SIZES.includes(buttonSize)) return null;

      if (theme === 'text') return null;

      const _hint = typeof hint === 'function' ? hint() : hint;

      if (typeof _hint === 'string') {
        return <span className={cx(css.hint, classNames?.hint)}>{_hint}</span>;
      }

      return _hint;
    };

    const renderLabelContent = () => {
      if (!label) return null;
      return (
        <div className={cx(css.wrapLabel, classNames?.labelContent)}>
          {renderLabel()}
          {renderHint()}
        </div>
      );
    };

    const renderContent = () => {
      return (
        <div className={cx(css.content, classNames?.content)}>
          {renderIcon()}
          {renderLabelContent()}
        </div>
      );
    };

    const renderLoading = () => {
      if (!loading) return null;
      return (
        <div className={css.wrapLoader}>
          <Loader className={css.loader} size={14} />
        </div>
      );
    };

    const ButtonContent = (
      <Fragment>
        {renderLoading()}
        {renderContent()}
      </Fragment>
    );

    const isButton = () => !(href && !onClick);

    const tag = isButton() ? 'button' : 'a';

    const buttonProps = {
      id,
      ...testId(test),
      name,
      className: cx(css.btn, classNames?.button, {
        [css[theme]]: true,
        [css.pill]: shape === 'pill',
        [css.onlyIcon]: icon && !label,
        [css.iconColor]: iconColor,
        [css.noPadding]: noPadding,
        [css.disabled]: disabled || loading,
        [css[`${_bgColor}Bg`]]: true,
      }),
      style: {
        '--l-h': `var(--s-${buttonSize})`,
        '--l-color-bg': bgColor,
        '--l-color-label': labelColor,
        '--l-icon-position': iconPosition === 'before' ? 'row' : 'row-reverse',
        '--l-hint-position': hintPosition === 'before' ? 'column-reverse' : 'column',
      },
      disabled: disabled || loading,
      role: a11y?.role,
      tabIndex: a11y?.tabIndex,
      'aria-label': a11y?.label,
      'aria-hidden': a11y?.hidden,
      'aria-expanded': a11y?.expanded,
      'aria-controls': a11y?.controls,
      'aria-haspopup': a11y?.haspopup,
      'data-loading': loading || undefined,
      ref,
      onKeyDown,
      ...(!isButton() && {
        rel: 'noreferrer',
        target: '_blank',
        href,
      }),
      ...(isButton() && {
        onClick: handleClick,
        value,
        type,
      }),
    };

    return createElement(tag, buttonProps, ButtonContent);
  },
);

export default Button;
